import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Link as LinkIcon,
  Bookmark,
  Share2,
  Linkedin,
  MapPin,
  ThumbsUp,
  ThumbsDown,
  Star,
  ChevronRight,
  LayoutDashboard,
  LogIn
} from 'lucide-react';
import { ENV } from '../../config/env';

// Types
interface SocialLink {
  platform: string;
  url: string;
}

interface TeamMember {
  id: string;
  name: string;
  role: string;
  avatarUrl?: string;
  socialLinks?: SocialLink[];
}

interface Review {
  id: string;
  authorName: string;
  authorRole: string;
  authorAvatar?: string;
  rating: number;
  content: string;
  likes: number;
  dislikes: number;
}

interface Job {
  _id: string;
  title: string;
  employmentType?: string;
  experienceLevel?: string;
  location?: string | { city?: string; state?: string; country?: string };
  salaryRange?: string;
  createdAt?: string;
  companyName?: string;
  companyLogo?: string;
}

interface PublicCompanyData {
  id: string;
  name: string;
  industry: string;
  website: string;
  description: string;
  logoUrl: string;
  phone: string;
  headquarters: string;
  companySize: string;
  foundedYear: string;
  aboutMe: string;
  socialLinks: SocialLink[];
  specialties: string[];
  hiringPreferences: string[];
  // Additional fields for public view
  teamMembers?: TeamMember[];
  reviews?: Review[];
  galleryImages?: string[];
  email?: string;
}

type TabType = 'about' | 'people' | 'overview' | 'jobs';

// Sub-components
const StatItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex flex-col gap-1">
    <span className="text-sm text-[#515151] font-['Manrope']">{label}</span>
    <span className="text-base font-medium text-[#353535] font-['Manrope'] truncate">{value || '-'}</span>
  </div>
);

const TabItem: React.FC<{ label: string; isActive: boolean; onClick: () => void }> = ({ label, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`pb-4 px-2 text-base font-medium whitespace-nowrap transition-all font-['Manrope'] ${
      isActive ? 'text-[#015FA6] border-b-2 border-[#015FA6]' : 'text-[#515151] hover:text-[#015FA6]'
    }`}
  >
    {label}
  </button>
);

const ProfileCard: React.FC<{ member: TeamMember }> = ({ member }) => (
  <div className="relative pt-10">
    <div className="bg-white border border-[#EDEDED] rounded-xl p-6 flex flex-col items-center shadow-sm">
      <div className="absolute -top-1 w-[74px] h-[74px] rounded-full border-2 border-[#EDEDED] overflow-hidden bg-gray-100">
        {member.avatarUrl ? (
          <img src={member.avatarUrl} alt={member.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#015FA6] to-[#0172C6] flex items-center justify-center text-white text-xl font-bold">
            {member.name.charAt(0)}
          </div>
        )}
      </div>
      <div className="mt-8 text-center">
        <h3 className="text-base font-medium text-[#0B111E] font-['Manrope'] leading-tight">{member.name}</h3>
        <p className="text-xs text-[#515151] font-['Manrope'] mt-1 mb-4">{member.role}</p>
        <div className="flex gap-3 justify-center">
          <Linkedin size={18} className="text-[#0077B5] cursor-pointer hover:opacity-80 transition-opacity" />
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#EA4C89" className="cursor-pointer hover:opacity-80 transition-opacity">
            <path d="M12 24C5.385 24 0 18.615 0 12S5.385 0 12 0s12 5.385 12 12-5.385 12-12 12zm5.768-9.768c-.236-.884-1.264-1.604-2.148-1.432-.648.132-1.224.672-1.668 1.128-.444-.456-1.02-.996-1.668-1.128-.884-.172-1.912.548-2.148 1.432-.312 1.164.312 2.304.888 3.12.636.912 1.392 1.752 2.148 2.544.18.192.468.492.78.492s.6-.3.78-.492c.756-.792 1.512-1.632 2.148-2.544.576-.816 1.2-1.956.888-3.12z"/>
          </svg>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="cursor-pointer hover:opacity-80 transition-opacity">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z" fill="#BC30A0"/>
            <path d="M12 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8z" fill="#BC30A0"/>
            <circle cx="18.406" cy="5.594" r="1.44" fill="#BC30A0"/>
          </svg>
        </div>
      </div>
    </div>
  </div>
);

const ReviewCard: React.FC<{ review: Review }> = ({ review }) => (
  <div className="bg-white border border-[#EDEDED] rounded-xl p-5 flex flex-col gap-4">
    {/* Quote icon and header */}
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <span className="text-[#015FA6] text-3xl font-serif">"</span>
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100">
            {review.authorAvatar ? (
              <img src={review.authorAvatar} alt={review.authorName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#015FA6] to-[#0172C6] flex items-center justify-center text-white text-sm font-bold">
                {review.authorName.charAt(0)}
              </div>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-[#0B111E] font-['Manrope']">{review.authorName}</p>
            <p className="text-xs text-[#515151] font-['Manrope']">{review.authorRole}</p>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Star size={14} className="text-[#F59E0B] fill-[#F59E0B]" />
        <span className="text-sm font-medium text-[#0B111E] font-['Manrope']">{review.rating}</span>
      </div>
    </div>

    {/* Review content */}
    <p className="text-sm text-[#0B111E] font-['Manrope'] leading-relaxed">
      {review.content}
    </p>

    {/* Likes/Dislikes */}
    <div className="flex items-center gap-4 pt-2 border-t border-[#EDEDED]">
      <button className="flex items-center gap-1 text-[#515151] hover:text-[#015FA6] transition-colors">
        <ThumbsUp size={16} />
        <span className="text-sm font-['Manrope']">{review.likes}</span>
      </button>
      <button className="flex items-center gap-1 text-[#515151] hover:text-red-500 transition-colors">
        <ThumbsDown size={16} />
        <span className="text-sm font-['Manrope']">{review.dislikes}</span>
      </button>
    </div>
  </div>
);

const JobCard: React.FC<{ job: Job; companyLogo?: string }> = ({ job, companyLogo }) => {
  const formatLocation = (location: Job['location']): string => {
    if (!location) return 'Not specified';
    if (typeof location === 'string') return location;
    return [location.city, location.state, location.country].filter(Boolean).join(', ') || 'Not specified';
  };

  const getTimeAgo = (dateStr?: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  return (
    <div className="bg-white border border-[#EDEDED] rounded-xl p-4 hover:shadow-md transition-shadow cursor-pointer">
      <div className="flex gap-3">
        {/* Company Logo */}
        <div className="w-12 h-12 rounded-lg border border-[#EDEDED] overflow-hidden flex-shrink-0 flex items-center justify-center">
          {companyLogo ? (
            <img src={companyLogo} alt="Company" className="w-full h-full object-contain" />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
              Logo
            </div>
          )}
        </div>

        {/* Job Info */}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-[#A5A5A5] font-['Manrope'] mb-1">{job.companyName || 'Company'}</p>
          <h4 className="text-base font-medium text-[#0B111E] font-['Manrope'] truncate">{job.title}</h4>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mt-2">
            {job.employmentType && (
              <span className="px-2 py-0.5 bg-[#E7F0FA] text-[#015FA6] text-xs font-['Manrope'] rounded">
                {job.employmentType}
              </span>
            )}
            {job.experienceLevel && (
              <span className="px-2 py-0.5 bg-[#FEF3C7] text-[#D97706] text-xs font-['Manrope'] rounded">
                {job.experienceLevel}
              </span>
            )}
          </div>

          {/* Location */}
          <div className="flex items-center gap-1 mt-2 text-[#353535]">
            <MapPin size={12} />
            <span className="text-xs font-['Manrope']">{formatLocation(job.location)}</span>
          </div>

          {/* Salary and Time */}
          <div className="flex items-center justify-between mt-2">
            <span className="text-sm font-medium text-[#015FA6] font-['Manrope']">
              {job.salaryRange || 'Salary not specified'}
            </span>
            <span className="text-xs text-[#757575] font-['Manrope']">{getTimeAgo(job.createdAt)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Public Company Profile Page
 * Displays company profile publicly accessible to all users (especially candidates)
 */
const PublicCompanyProfile: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();

  const [company, setCompany] = useState<PublicCompanyData | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('about');
  const [isFollowing, setIsFollowing] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status
  useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    const authUser = localStorage.getItem('authUser');

    if (authToken && authUser) {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (companyId) {
      fetchCompanyData();
      fetchCompanyJobs();
    }
  }, [companyId]);

  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${ENV.COMPANY_SERVICE_URL}/companies/v1/public/${companyId}`);

      if (!response.ok) {
        // Try alternative endpoint
        const altResponse = await fetch(`${ENV.COMPANY_SERVICE_URL}/companies/v1/${companyId}`);
        if (!altResponse.ok) {
          throw new Error('Company not found');
        }
        const altData = await altResponse.json();
        setCompany(altData.payload || altData);
        return;
      }

      const data = await response.json();
      setCompany(data.payload || data);
    } catch (err) {
      console.error('Failed to fetch company:', err);
      setError(err instanceof Error ? err.message : 'Failed to load company');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyJobs = async () => {
    try {
      const response = await fetch(`${ENV.JOB_SERVICE_URL}/jobs/v1/company/${companyId}`);
      if (response.ok) {
        const data = await response.json();
        setJobs(data.payload || data.data || data || []);
      }
    } catch (err) {
      console.error('Failed to fetch company jobs:', err);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleFollow = () => {
    setIsFollowing(!isFollowing);
    // TODO: API call to follow/unfollow
  };

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
    // TODO: API call to bookmark
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: company?.name,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      // TODO: Show toast notification
    }
  };

  // Sample data for demo (would come from API)
  const sampleTeamMembers: TeamMember[] = company?.teamMembers || [
    { id: '1', name: 'Cameron Williamson', role: 'Product Analyst' },
    { id: '2', name: 'Cody Fisher', role: 'Data Analyst Lead' },
    { id: '3', name: 'Brooklyn Simmons', role: 'Senior Interaction Designer' },
    { id: '4', name: 'Kristin Watson', role: 'Head of Product Design' },
    { id: '5', name: 'Darrell Steward', role: 'Head of Engineering' },
  ];

  const sampleReviews: Review[] = company?.reviews || [
    {
      id: '1',
      authorName: 'Leslie Alexander',
      authorRole: 'Job Seeker',
      rating: 4.5,
      content: 'When I sent in my application to Sandro, I was simply hoping for a chance. What I got was a masterclass in how to run a thoughtful, respectful, and inspiring hiring process. From the way they asked questions to the way they listened—every detail reflected.',
      likes: 14,
      dislikes: 0,
    },
    {
      id: '2',
      authorName: 'Wade Warren',
      authorRole: 'Job Seeker',
      rating: 4.5,
      content: 'Taint stood out to me because of their communication and transparency. I always knew where I stood, what the next steps were, and what they were looking for. More importantly, I felt like they truly wanted a two-way dialogue—not just to assess.',
      likes: 56,
      dislikes: 1,
    },
    {
      id: '3',
      authorName: 'Ronald Richards',
      authorRole: 'Job Seeker',
      rating: 4.5,
      content: "I've always believed that the best companies are the ones that make you feel seen, even before you join them. That was exactly my experience with Taint. The interview wasn't just about evaluating me—it was about helping me evaluate them, too.",
      likes: 65,
      dislikes: 7,
    },
  ];

  const sampleGalleryImages = company?.galleryImages || [
    '/company/office1.jpg',
    '/company/office2.jpg',
    '/company/office3.jpg',
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#015FA6] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg font-medium text-[#757575] font-['Manrope']">Loading company profile...</p>
        </div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-[#0B111E] font-['Manrope'] mb-2">Company Not Found</h2>
          <p className="text-base text-[#757575] font-['Manrope'] mb-4">{error}</p>
          <button
            onClick={handleBack}
            className="px-6 py-3 bg-[#015FA6] text-white font-medium rounded-lg hover:bg-[#014a85] transition-colors font-['Manrope']"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen h-screen overflow-y-auto bg-[#F8FAFC] font-['Manrope'] pb-20">
      {/* Top Navigation Bar */}
      <div className="max-w-[1200px] mx-auto pt-6 px-4 flex items-center justify-between">
        {/* Left side - Back/Dashboard button */}
        {isAuthenticated ? (
          <button
            onClick={() => navigate('/auth/login')}
            className="flex items-center gap-2 px-4 py-2 text-[#015FA6] hover:bg-[#E7F0FA] rounded-lg transition-colors font-medium"
          >
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </button>
        ) : (
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-[#015FA6]" />
          </button>
        )}

        {/* Right side - Login button (only for non-authenticated users) */}
        {!isAuthenticated && (
          <button
            onClick={() => navigate('/auth/login')}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#015FA6] text-white rounded-lg hover:bg-[#014a85] transition-colors font-medium"
          >
            <LogIn className="w-4 h-4" />
            <span>Login</span>
          </button>
        )}
      </div>

      {/* Main Container */}
      <main className="max-w-[1200px] mx-auto mt-4 px-4">

        {/* Header Card */}
        <section className="bg-white border border-[#EDEDED] rounded-2xl p-6 shadow-sm">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Logo */}
            <div className="w-[120px] h-[120px] md:w-[140px] md:h-[140px] border border-[#EDEDED] rounded-xl flex items-center justify-center p-3 flex-shrink-0">
              {company.logoUrl ? (
                <img src={company.logoUrl} alt={company.name} className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#015FA6] to-[#0172C6] rounded-lg flex items-center justify-center text-white text-4xl font-bold">
                  {company.name?.charAt(0) || 'C'}
                </div>
              )}
            </div>

            {/* Company Info */}
            <div className="flex-1 w-full">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 w-full">
                <div>
                  <h1 className="text-2xl md:text-3xl font-semibold text-[#0B111E]">{company.name}</h1>
                  {company.website && (
                    <a
                      href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 mt-1 text-[#015FA6] text-sm hover:underline"
                    >
                      <LinkIcon size={14} />
                      {company.website.replace(/^https?:\/\//, '')}
                    </a>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleFollow}
                    className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                      isFollowing
                        ? 'bg-[#E7F0FA] text-[#015FA6] border border-[#015FA6]'
                        : 'bg-[#015FA6] text-white hover:bg-[#014a85]'
                    }`}
                  >
                    {isFollowing ? 'Following' : 'Follow'}
                  </button>
                  <button
                    onClick={handleBookmark}
                    className={`p-2 rounded-lg border transition-colors ${
                      isBookmarked ? 'border-[#015FA6] bg-[#E7F0FA]' : 'border-[#EDEDED] hover:border-[#015FA6]'
                    }`}
                  >
                    <Bookmark size={20} className={isBookmarked ? 'text-[#015FA6] fill-[#015FA6]' : 'text-[#515151]'} />
                  </button>
                  <button
                    onClick={handleShare}
                    className="p-2 rounded-lg border border-[#EDEDED] hover:border-[#015FA6] transition-colors"
                  >
                    <Share2 size={20} className="text-[#515151]" />
                  </button>
                </div>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 mt-6 pt-6 border-t border-[#EDEDED]">
                <StatItem label="Location" value={company.headquarters || '-'} />
                <StatItem label="Company size" value={company.companySize || '-'} />
                <StatItem label="Email" value={company.email || '-'} />
                <StatItem label="Phone" value={company.phone || '-'} />
              </div>
            </div>
          </div>
        </section>

        {/* Tab Navigation */}
        <nav className="mt-10 border-b border-[#CBCBCB] flex gap-6 overflow-x-auto">
          <TabItem label="About Company" isActive={activeTab === 'about'} onClick={() => setActiveTab('about')} />
          <TabItem label={`People at ${company.name}`} isActive={activeTab === 'people'} onClick={() => setActiveTab('people')} />
          <TabItem label="Overview" isActive={activeTab === 'overview'} onClick={() => setActiveTab('overview')} />
          <TabItem label={`Jobs From ${company.name}`} isActive={activeTab === 'jobs'} onClick={() => setActiveTab('jobs')} />
        </nav>

        {/* Tab Content */}
        <div className="mt-8">
          {/* About Company Tab */}
          {activeTab === 'about' && (
            <div className="space-y-12">
              {/* Company Information */}
              <section>
                <h2 className="text-2xl font-semibold text-[#0B111E] mb-4">Company Informations</h2>
                <p className="text-[#0B111E] leading-[1.7] text-base">
                  {company.description || 'No company description available.'}
                </p>
              </section>

              {/* Gallery */}
              <section>
                <div className="flex flex-col md:flex-row gap-4 h-auto md:h-[350px]">
                  <div className="flex flex-col gap-4 w-full md:w-[350px]">
                    <div className="flex-1 bg-gray-200 rounded-xl overflow-hidden min-h-[160px]">
                      <img
                        src={sampleGalleryImages[0]}
                        alt="Office"
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-xl overflow-hidden min-h-[160px]">
                      <img
                        src={sampleGalleryImages[1]}
                        alt="Office"
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                    </div>
                  </div>
                  <div className="flex-1 bg-gray-200 rounded-xl overflow-hidden min-h-[350px]">
                    <img
                      src={sampleGalleryImages[2]}
                      alt="Office"
                      className="w-full h-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                    />
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* People Tab */}
          {activeTab === 'people' && (
            <section>
              <h2 className="text-2xl font-semibold text-[#0B111E] mb-8">People at {company.name}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                {sampleTeamMembers.map((member) => (
                  <ProfileCard key={member.id} member={member} />
                ))}
              </div>
            </section>
          )}

          {/* Overview (Reviews) Tab */}
          {activeTab === 'overview' && (
            <section>
              <h2 className="text-2xl font-semibold text-[#0B111E] mb-8">Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {sampleReviews.map((review) => (
                  <ReviewCard key={review.id} review={review} />
                ))}
              </div>
            </section>
          )}

          {/* Jobs Tab */}
          {activeTab === 'jobs' && (
            <section>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-semibold text-[#0B111E]">Jobs from {company.name}</h2>
                <button className="flex items-center gap-1 text-[#015FA6] text-sm font-medium hover:underline">
                  See all <ChevronRight size={16} />
                </button>
              </div>

              {jobs.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {jobs.map((job) => (
                    <JobCard key={job._id} job={{...job, companyName: company.name}} companyLogo={company.logoUrl} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-xl border border-[#EDEDED]">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="mx-auto mb-3 text-gray-300">
                    <path d="M20 6H16V4C16 2.89 15.11 2 14 2H10C8.89 2 8 2.89 8 4V6H4C2.89 6 2 6.89 2 8V19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM10 4H14V6H10V4Z" fill="currentColor"/>
                  </svg>
                  <p className="text-[#757575] font-['Manrope']">No open positions at the moment</p>
                </div>
              )}
            </section>
          )}
        </div>

        {/* People Section - Always visible at bottom when on About tab */}
        {activeTab === 'about' && (
          <>
            <section className="mt-16">
              <h2 className="text-2xl font-semibold text-[#0B111E] mb-8">People at {company.name}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                {sampleTeamMembers.map((member) => (
                  <ProfileCard key={member.id} member={member} />
                ))}
              </div>
            </section>

            {/* Overview/Reviews Section */}
            <section className="mt-16">
              <h2 className="text-2xl font-semibold text-[#0B111E] mb-8">Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {sampleReviews.map((review) => (
                  <ReviewCard key={review.id} review={review} />
                ))}
              </div>
            </section>

            {/* Jobs Section */}
            <section className="mt-16">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-semibold text-[#0B111E]">Jobs from {company.name}</h2>
                <button
                  onClick={() => setActiveTab('jobs')}
                  className="flex items-center gap-1 text-[#015FA6] text-sm font-medium hover:underline"
                >
                  See all <ChevronRight size={16} />
                </button>
              </div>

              {jobs.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {jobs.slice(0, 6).map((job) => (
                    <JobCard key={job._id} job={{...job, companyName: company.name}} companyLogo={company.logoUrl} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-xl border border-[#EDEDED]">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="mx-auto mb-3 text-gray-300">
                    <path d="M20 6H16V4C16 2.89 15.11 2 14 2H10C8.89 2 8 2.89 8 4V6H4C2.89 6 2 6.89 2 8V19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM10 4H14V6H10V4Z" fill="currentColor"/>
                  </svg>
                  <p className="text-[#757575] font-['Manrope']">No open positions at the moment</p>
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default PublicCompanyProfile;
